import { useCallback, useEffect, useRef, useState } from "react";
import {
  FaBolt,
  FaBullseye,
  FaChartPie,
  FaCheckCircle,
  FaEdit,
  FaEye,
  FaLightbulb,
  FaPlus,
  FaRegFrown,
  FaRegMeh,
  FaRegSmile,
  FaRobot,
  FaTimes,
  FaUserCircle,
} from "react-icons/fa";
import { LuToggleLeft, LuToggleRight } from "react-icons/lu";

const DEMO_TIMING = {
  highlightMs: 1400,
  openHoldMs: 7800,
  closedHoldMs: 1600,
  animMs: 220,
  scrollPauseTopMs: 900,
  scrollDurationMs: 4800,
};

const ANALYSIS_DATA = {
  postTitle: "Morning routine reel",
  score: 76,
  engagement: "Medium",
  comments: 128,
  positive: 82,
  neutral: 28,
  negative: 18,
  positivePct: 64,
  neutralPct: 22,
  negativePct: 14,
  summary:
    "Most viewers react positively to the routine tips, while a smaller group asks product questions or flags lighting and pacing issues.",
  persona: "Curious lifestyle viewers who want quick, practical takeaways",
  themes: ["Routine Tips", "Product Asks", "Editing Feedback"],
  insights: [
    "About 64% of comments are positive, driven by praise for the morning checklist and energy.",
    "Neutral replies are mostly product and pricing questions waiting for a clearer CTA.",
    "Negative feedback clusters around lighting, caption length, and a few spam-like replies.",
  ],
  actions: [
    "Pin a comment with product links and a short morning checklist to convert curious viewers.",
    "Ask one clear question in the next caption to turn neutral comments into conversations.",
  ],
};

const ENGAGEMENT_TONE = {
  High: "bg-emerald-50 text-emerald-700 ring-emerald-100",
  Medium: "bg-amber-50 text-amber-700 ring-amber-100",
  Low: "bg-rose-50 text-rose-700 ring-rose-100",
};

const ROWS = [
  {
    id: 1,
    title: "Morning routine reel",
    type: "Direct Message",
    keywords: ["routine", "tips", "link"],
    isActive: true,
  },
  {
    id: 2,
    title: "Brand launch Q&A",
    type: "Direct Message",
    keywords: ["launch", "qa"],
    isActive: true,
  },
  {
    id: 3,
    title: "Summer drop teaser",
    type: "Direct Message",
    keywords: ["shop", "deal"],
    isActive: true,
  },
  {
    id: 4,
    title: "Collab reel with Mira",
    type: "Direct Message",
    keywords: ["collab", "code"],
    isActive: true,
  },
  {
    id: 5,
    title: "Old giveaway wrap-up",
    type: "Direct Message",
    keywords: ["win", "entry"],
    isActive: false,
  },
  {
    id: 6,
    title: "Weekend travel vlog",
    type: "Direct Message",
    keywords: ["travel", "map"],
    isActive: false,
  },
];

/**
 * Marketing mockup only — mirrors Automation Dashboard + Comment Analysis,
 * with smaller type and an auto product-tour loop. No API calls.
 */
const AutomationShowcaseMockup = () => {
  const rootRef = useRef(null);
  const analysisBodyRef = useRef(null);
  const timersRef = useRef([]);
  const rafRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | highlight | open
  const [analysisVisible, setAnalysisVisible] = useState(false);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
    if (rafRef.current) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const schedule = useCallback((fn, ms) => {
    const id = window.setTimeout(fn, ms);
    timersRef.current.push(id);
  }, []);

  useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) =>
        setInView(entry.isIntersecting && entry.intersectionRatio > 0.3),
      { threshold: [0, 0.3, 0.55] }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    clearTimers();
    const reducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (!inView) {
      setPhase("idle");
      setAnalysisVisible(false);
      return undefined;
    }

    if (reducedMotion) {
      setPhase("open");
      setAnalysisVisible(true);
      return undefined;
    }

    let cancelled = false;

    const runCycle = () => {
      if (cancelled) return;
      setPhase("idle");
      setAnalysisVisible(false);

      schedule(() => {
        if (cancelled) return;
        setPhase("highlight");

        schedule(() => {
          if (cancelled) return;
          setPhase("open");
          requestAnimationFrame(() => {
            if (!cancelled) setAnalysisVisible(true);
          });

          schedule(() => {
            if (cancelled) return;
            setAnalysisVisible(false);
            schedule(() => {
              if (cancelled) return;
              setPhase("idle");
              schedule(runCycle, DEMO_TIMING.closedHoldMs);
            }, DEMO_TIMING.animMs);
          }, DEMO_TIMING.openHoldMs);
        }, DEMO_TIMING.highlightMs);
      }, DEMO_TIMING.closedHoldMs);
    };

    runCycle();
    return () => {
      cancelled = true;
      clearTimers();
    };
  }, [inView, clearTimers, schedule]);

  // Auto-scroll the Comment Analysis body while the overlay is open
  useEffect(() => {
    const el = analysisBodyRef.current;
    if (!analysisVisible || !el) return undefined;

    const reducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    el.scrollTop = 0;
    if (reducedMotion) return undefined;

    let cancelled = false;
    let startTime = 0;

    const easeInOut = (t) =>
      t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

    const startScroll = () => {
      if (cancelled || !analysisBodyRef.current) return;
      const node = analysisBodyRef.current;
      const maxScroll = Math.max(0, node.scrollHeight - node.clientHeight);
      if (maxScroll < 8) return;

      startTime = performance.now();

      const tick = (now) => {
        if (cancelled || !analysisBodyRef.current) return;
        const progress = Math.min(
          1,
          (now - startTime) / DEMO_TIMING.scrollDurationMs
        );
        analysisBodyRef.current.scrollTop = maxScroll * easeInOut(progress);
        if (progress < 1) {
          rafRef.current = window.requestAnimationFrame(tick);
        }
      };

      rafRef.current = window.requestAnimationFrame(tick);
    };

    const pauseId = window.setTimeout(startScroll, DEMO_TIMING.scrollPauseTopMs);
    return () => {
      cancelled = true;
      window.clearTimeout(pauseId);
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [analysisVisible]);

  const analysisOpen = phase === "open";

  // Block user scroll/interaction so only the auto-demo moves the panel
  useEffect(() => {
    const node = rootRef.current;
    if (!node) return undefined;
    const block = (e) => e.preventDefault();
    node.addEventListener("wheel", block, { passive: false });
    node.addEventListener("touchmove", block, { passive: false });
    return () => {
      node.removeEventListener("wheel", block);
      node.removeEventListener("touchmove", block);
    };
  }, []);

  return (
    <div
      ref={rootRef}
      className="relative cursor-none select-none overflow-hidden rounded-2xl border border-gray-200 bg-gray-100 shadow-lg"
    >
      <div className="flex min-h-[380px] sm:min-h-[440px] md:min-h-[480px]">
        <aside
          aria-hidden
          className="hidden shrink-0 flex-col items-center gap-2.5 border-r border-gray-200 bg-white py-4 sm:flex sm:w-11 md:w-14 md:gap-3 md:py-5"
        >
          <span className="h-5 w-5 rounded-md bg-orange-100 md:h-6 md:w-6" />
          {[0, 1, 2, 3, 4].map((item) => (
            <span
              key={item}
              className={`h-4 w-4 rounded-md md:h-5 md:w-5 ${
                item === 2 ? "bg-orange-100 ring-1 ring-orange-200" : "bg-gray-100"
              }`}
            />
          ))}
          <span className="mt-auto h-7 w-7 rounded-full bg-gray-200 md:h-8 md:w-8" />
        </aside>

        <div className="min-w-0 flex-1 overflow-hidden p-2.5 sm:p-3 md:p-4">
          <div className="mb-2.5 flex flex-col gap-2 rounded-xl border border-gray-200 bg-white p-2.5 sm:mb-3 sm:flex-row sm:flex-nowrap sm:items-center sm:justify-between sm:gap-3 sm:rounded-2xl sm:p-3 md:p-3.5">
            <div className="min-w-0 flex-1">
              <h3 className="text-xs font-bold text-gray-900 sm:text-sm md:text-base">
                Automation Dashboard
              </h3>
              <p className="mt-0.5 text-[9px] leading-snug text-gray-500 sm:text-[10px] md:text-xs">
                Manage your Instagram automations and unlock AI-powered audience insights.
              </p>
            </div>
            <span className="inline-flex w-full shrink-0 items-center justify-center gap-1 rounded-lg bg-orange-500 px-2 py-1.5 text-[9px] font-medium text-white sm:w-auto sm:rounded-xl sm:px-2.5 sm:text-[10px] md:px-3 md:py-2 md:text-xs">
              <FaPlus className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
              Create New Automation
            </span>
          </div>

          <div className="mb-2.5 grid grid-cols-2 gap-1.5 sm:mb-3 sm:gap-2 md:grid-cols-3">
            <StatCard label="Total Automations" value="6" Icon={FaRobot} tone="default" />
            <StatCard label="Active" value="4" Icon={FaBolt} tone="success" />
            <StatCard label="Inactive" value="2" Icon={FaTimes} tone="muted" />
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm sm:rounded-2xl">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="sticky top-0 z-10 bg-gray-50 shadow-sm">
                  <tr>
                    {[
                      "S.N",
                      "Post Title",
                      "Type",
                      "Keywords",
                      "Status",
                      "AI Comment Analysis",
                      "Actions",
                    ].map((h) => (
                      <th
                        key={h}
                        className="whitespace-nowrap px-2 py-2 text-left text-[8px] font-semibold uppercase tracking-wider text-gray-500 sm:px-3 sm:py-2.5 sm:text-[9px] md:px-4 md:text-[10px]"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {ROWS.map((row, index) => (
                    <tr
                      key={row.id}
                      className={
                        phase === "highlight" && index === 0
                          ? "bg-orange-50/60"
                          : "bg-white"
                      }
                    >
                      <td className="whitespace-nowrap px-2 py-2 text-[9px] font-medium text-gray-500 sm:px-3 sm:text-[10px] md:px-4 md:text-xs">
                        {index + 1}
                      </td>
                      <td className="max-w-[88px] truncate px-2 py-2 text-[9px] font-semibold text-gray-900 sm:max-w-[120px] sm:px-3 sm:text-[10px] md:max-w-[140px] md:px-4 md:text-xs">
                        {row.title}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 sm:px-3 md:px-4">
                        <span className="inline-flex rounded-full bg-blue-50 px-1.5 py-0.5 text-[8px] font-medium text-blue-700 ring-1 ring-inset ring-blue-100 sm:text-[9px]">
                          {row.type}
                        </span>
                      </td>
                      <td className="px-2 py-2 sm:px-3 md:px-4">
                        <div className="flex max-w-[88px] flex-wrap gap-0.5 sm:max-w-[100px]">
                          {row.keywords.map((k) => (
                            <span
                              key={k}
                              className="rounded bg-gray-100 px-1 py-0.5 text-[8px] font-medium text-gray-700 sm:text-[9px]"
                            >
                              {k}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 sm:px-3 md:px-4">
                        <div className="flex items-center gap-1">
                          {row.isActive ? (
                            <LuToggleRight className="h-4 w-4 text-green-500 sm:h-5 sm:w-5" />
                          ) : (
                            <LuToggleLeft className="h-4 w-4 text-gray-300 sm:h-5 sm:w-5" />
                          )}
                          <span
                            className={`text-[8px] font-semibold sm:text-[9px] md:text-[10px] ${
                              row.isActive ? "text-green-600" : "text-gray-400"
                            }`}
                          >
                            {row.isActive ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 sm:px-3 md:px-4">
                        <span
                          className={`relative inline-flex items-center gap-1 rounded-md border border-orange-200 bg-orange-50 px-1.5 py-1 text-[8px] font-semibold text-orange-600 sm:text-[9px] md:text-[10px] ${
                            phase === "highlight" && index === 0
                              ? "animate-pulse ring-2 ring-orange-400 ring-offset-1"
                              : ""
                          }`}
                        >
                          <FaChartPie className="h-2.5 w-2.5" />
                          Analyze
                          {phase === "highlight" && index === 0 && <GuideCursor />}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 sm:px-3 md:px-4">
                        <div className="flex items-center gap-0.5">
                          <span className="rounded p-1 text-orange-600">
                            <FaEye className="h-3 w-3" />
                          </span>
                          <span
                            className={`rounded p-1 ${
                              row.isActive ? "text-blue-600" : "text-gray-300"
                            }`}
                          >
                            <FaEdit className="h-3 w-3" />
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay — complete Comment Analysis (smaller text) */}
      {analysisOpen && (
        <div
          className={`absolute inset-0 z-20 flex items-center justify-center bg-black/45 p-2.5 transition-opacity duration-200 sm:p-3 ${
            analysisVisible ? "opacity-100" : "opacity-0"
          }`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="showcase-analysis-title"
        >
          <div
            className={`flex max-h-[96%] w-full max-w-[22rem] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl transition-all duration-200 sm:max-w-md sm:rounded-2xl ${
              analysisVisible
                ? "translate-y-0 scale-100 opacity-100"
                : "translate-y-3 scale-95 opacity-0"
            }`}
          >
            {/* Header */}
            <div className="flex shrink-0 items-start justify-between gap-2 border-b border-gray-100 px-3 py-2.5">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <FaChartPie className="h-3 w-3 shrink-0 text-orange-600" />
                  <p
                    id="showcase-analysis-title"
                    className="text-xs font-bold text-gray-900"
                  >
                    Comment Analysis
                  </p>
                  <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[8px] font-semibold text-gray-600">
                    Cached
                  </span>
                </div>
                <p className="mt-0.5 truncate text-[9px] text-gray-500">
                  {ANALYSIS_DATA.postTitle} · {ANALYSIS_DATA.comments} comments
                  analyzed
                </p>
              </div>
              <span className="rounded-md p-1 text-gray-400">
                <FaTimes className="h-3 w-3" />
              </span>
            </div>

            {/* Body */}
            <div
              ref={analysisBodyRef}
              className="pointer-events-none min-h-0 flex-1 space-y-2.5 overflow-y-auto overscroll-contain px-3 py-2.5"
            >
              <div className="grid grid-cols-3 gap-1 sm:gap-1.5">
                <div className="flex flex-col items-center justify-center rounded-md border border-orange-100 bg-gradient-to-br from-orange-50 to-amber-50 px-1 py-1.5">
                  <p className="text-[7px] font-semibold uppercase tracking-wide text-orange-600 sm:text-[8px]">
                    Score
                  </p>
                  <p className="mt-0.5 text-sm font-extrabold leading-none text-gray-900 sm:text-base">
                    {ANALYSIS_DATA.score}
                  </p>
                  <p className="mt-0.5 text-[7px] text-gray-500 sm:text-[8px]">/ 100</p>
                </div>
                <div className="flex flex-col items-center justify-center rounded-md border border-gray-100 bg-gray-50 px-1 py-1.5">
                  <p className="text-[7px] font-semibold uppercase tracking-wide text-gray-500 sm:text-[8px]">
                    Engage
                  </p>
                  <span
                    className={`mt-0.5 inline-flex items-center gap-0.5 rounded-full px-1 py-0.5 text-[8px] font-bold ring-1 ring-inset sm:text-[9px] ${
                      ENGAGEMENT_TONE[ANALYSIS_DATA.engagement] ||
                      ENGAGEMENT_TONE.Medium
                    }`}
                  >
                    <FaBolt className="h-2 w-2" />
                    {ANALYSIS_DATA.engagement}
                  </span>
                </div>
                <div className="flex flex-col items-center justify-center rounded-md border border-gray-100 bg-gray-50 px-1 py-1.5">
                  <p className="text-[7px] font-semibold uppercase tracking-wide text-gray-500 sm:text-[8px]">
                    Comments
                  </p>
                  <p className="mt-0.5 text-sm font-bold leading-none text-gray-900 sm:text-base">
                    {ANALYSIS_DATA.comments}
                  </p>
                </div>
              </div>

              <div>
                <div className="grid grid-cols-3 gap-1 sm:gap-1.5">
                  <SentimentCard
                    label="Positive"
                    value={ANALYSIS_DATA.positive}
                    pct={ANALYSIS_DATA.positivePct}
                    tone="positive"
                    Icon={FaRegSmile}
                  />
                  <SentimentCard
                    label="Neutral"
                    value={ANALYSIS_DATA.neutral}
                    pct={ANALYSIS_DATA.neutralPct}
                    tone="neutral"
                    Icon={FaRegMeh}
                  />
                  <SentimentCard
                    label="Negative"
                    value={ANALYSIS_DATA.negative}
                    pct={ANALYSIS_DATA.negativePct}
                    tone="negative"
                    Icon={FaRegFrown}
                  />
                </div>
                <div className="mt-1.5 flex h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="bg-emerald-500"
                    style={{ width: `${ANALYSIS_DATA.positivePct}%` }}
                  />
                  <div
                    className="bg-gray-300"
                    style={{ width: `${ANALYSIS_DATA.neutralPct}%` }}
                  />
                  <div
                    className="bg-rose-500"
                    style={{ width: `${ANALYSIS_DATA.negativePct}%` }}
                  />
                </div>
              </div>

              <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-2">
                <div className="flex items-center gap-1 text-[8px] font-semibold uppercase tracking-wide text-blue-700">
                  <FaCheckCircle className="h-2.5 w-2.5" />
                  Summary
                </div>
                <p className="mt-1 text-[9px] leading-snug text-gray-700">
                  {ANALYSIS_DATA.summary}
                </p>
              </div>

              <div className="rounded-lg border border-orange-100 bg-orange-50/50 p-2">
                <div className="flex items-center gap-1 text-[8px] font-semibold uppercase tracking-wide text-orange-700">
                  <FaUserCircle className="h-2.5 w-2.5" />
                  Audience Persona
                </div>
                <p className="mt-1 text-[9px] leading-snug text-gray-700">
                  {ANALYSIS_DATA.persona}
                </p>
              </div>

              <div>
                <div className="flex items-center gap-1 text-[8px] font-semibold uppercase tracking-wide text-gray-500">
                  <FaBullseye className="h-2.5 w-2.5 text-purple-500" />
                  Top Themes
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {ANALYSIS_DATA.themes.map((theme) => (
                    <span
                      key={theme}
                      className="rounded-full bg-purple-50 px-2 py-0.5 text-[8px] font-medium text-purple-700 ring-1 ring-inset ring-purple-100"
                    >
                      {theme}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-1 text-[8px] font-semibold uppercase tracking-wide text-gray-500">
                  <FaLightbulb className="h-2.5 w-2.5 text-amber-500" />
                  Key Insights
                </div>
                <ul className="mt-1 space-y-1">
                  {ANALYSIS_DATA.insights.map((insight, idx) => (
                    <li
                      key={insight}
                      className="flex gap-1.5 rounded-md border border-gray-100 bg-gray-50 p-1.5 text-[9px] leading-snug text-gray-700"
                    >
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-100 text-[8px] font-bold text-amber-700">
                        {idx + 1}
                      </span>
                      <span>{insight}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <div className="flex items-center gap-1 text-[8px] font-semibold uppercase tracking-wide text-gray-500">
                  <FaBolt className="h-2.5 w-2.5 text-emerald-500" />
                  Recommended Actions
                </div>
                <ul className="mt-1 space-y-1">
                  {ANALYSIS_DATA.actions.map((action) => (
                    <li
                      key={action}
                      className="flex items-start gap-1.5 rounded-md border border-emerald-100 bg-emerald-50/60 p-1.5 text-[9px] leading-snug text-gray-700"
                    >
                      <FaCheckCircle className="mt-0.5 h-3 w-3 shrink-0 text-emerald-500" />
                      <span>{action}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const GuideCursor = () => (
  <span
    aria-hidden
    className="pointer-events-none absolute -bottom-1 -right-1 h-3 w-3 translate-x-1 translate-y-1 rounded-full border-2 border-white bg-orange-500 shadow-md"
    style={{ animation: "showcase-cursor-tap 1.1s ease-in-out infinite" }}
  >
    <style>{`
      @keyframes showcase-cursor-tap {
        0%, 100% { transform: translate(2px, 2px) scale(1); opacity: 1; }
        50% { transform: translate(0px, 0px) scale(0.85); opacity: 0.85; }
      }
    `}</style>
  </span>
);

const StatCard = ({ label, value, Icon, tone }) => {
  const tones = {
    default: "border-gray-200 bg-white text-gray-600",
    success: "border-emerald-100 bg-emerald-50 text-emerald-600",
    muted: "border-gray-200 bg-gray-50 text-gray-400",
  };

  return (
    <div className={`min-w-0 rounded-xl border p-2 shadow-sm sm:p-2.5 md:p-3 ${tones[tone]}`}>
      <div className="flex items-center justify-between gap-1.5">
        <div className="min-w-0">
          <p className="truncate text-[9px] text-gray-500 sm:text-[10px] md:text-xs">{label}</p>
          <p className="mt-0.5 truncate text-sm font-bold text-gray-900 sm:text-base md:text-lg">
            {value}
          </p>
        </div>
        <Icon className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
      </div>
    </div>
  );
};

const SentimentCard = ({ label, value, pct, tone, Icon }) => {
  const tones = {
    positive: "border-emerald-100 bg-emerald-50 text-emerald-700",
    neutral: "border-gray-100 bg-gray-50 text-gray-600",
    negative: "border-rose-100 bg-rose-50 text-rose-700",
  };

  return (
    <div className={`rounded-md border px-1 py-1.5 text-center sm:px-1.5 sm:py-2 ${tones[tone]}`}>
      <Icon className="mx-auto h-2.5 w-2.5 sm:h-3 sm:w-3" />
      <p className="mt-0.5 text-xs font-bold leading-none sm:text-sm">{value}</p>
      <p className="mt-0.5 text-[7px] font-medium uppercase tracking-wide sm:text-[8px]">
        {label}
      </p>
      <p className="text-[7px] opacity-70 sm:text-[8px]">{pct}%</p>
    </div>
  );
};

export default AutomationShowcaseMockup;
